import { Controller } from "@hotwired/stimulus"

const MEDIA_SELECTOR = '[data-media-player-target="media"]'

// Swaps a Virtual Meeting session's recording for its audio and back (TMS-1825), in the same
// place on the page. The listener carries on where they were: the position moves across, and so
// does whether it was playing. Progress is recorded from whichever of the two is showing.
//
// The page opens on the recording. Only the audio player's own previous and next open a session
// in audio (`?mode=audio`, and then it starts playing), and that marker leaves the address bar
// once the page has loaded, so a reload or a shared link opens on the recording again. A session
// whose video hasn't arrived yet shows its audio alone, with nothing to switch to.
export default class extends Controller {
  static targets = ["videoPanel", "audioPanel", "toggle", "videoTranscript", "audioTranscript", "playCue"]
  static values = {
    mode: { type: String, default: "video" },
    autoplay: { type: Boolean, default: false }
  }
  static outlets = ["virtual-session-progress"]

  connect() {
    this.forgetAudioMode()
    if (this.autoplayValue && this.listening) this.playWhenReady(this.audio)
  }

  virtualSessionProgressOutletConnected(progress) {
    progress.useMedia(this.liveMedia)
  }

  toggle() {
    const from = this.liveMedia
    if (!from) return

    const place = { position: from.currentTime, playing: !from.paused && !from.ended }
    from.pause()

    this.modeValue = this.listening ? "video" : "audio"
    this.showLiveMedia()
    this.carryOn(this.liveMedia, place)
  }

  showLiveMedia() {
    this.videoPanelTarget.hidden = this.listening
    this.audioPanelTarget.hidden = !this.listening
    this.toggleTarget.textContent = this.listening ? "Back to video" : "Audio Only"
    // Each file has its own transcript, timed to it.
    if (this.hasVideoTranscriptTarget) this.videoTranscriptTarget.hidden = this.listening
    if (this.hasAudioTranscriptTarget) this.audioTranscriptTarget.hidden = !this.listening
    if (this.hasVirtualSessionProgressOutlet) this.virtualSessionProgressOutlet.useMedia(this.liveMedia)
  }

  // Seeks once the media knows its length — a file not loaded yet would drop an earlier seek —
  // and after the player's own opening position, which it applies at the same moment. The other
  // file can be a little shorter, so the place is kept inside it.
  carryOn(media, { position, playing }) {
    const seekAndPlay = () => {
      const length = media.duration
      media.currentTime = Number.isFinite(length) && length > 0 ? Math.min(position, length) : position
      if (playing) this.play(media)
    }

    if (media.readyState >= HTMLMediaElement.HAVE_METADATA) {
      seekAndPlay()
    } else {
      media.addEventListener("loadedmetadata", seekAndPlay, { once: true })
    }
  }

  playWhenReady(media) {
    if (!media) return

    if (media.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      this.play(media)
    } else {
      media.addEventListener("canplay", () => this.play(media), { once: true })
    }
  }

  // Arriving from the previous or next session, the browser may refuse to start playing on its
  // own (Safari and iOS always do on a new page); the player then waits, paused, and says so
  // until the listener presses play.
  play(media) {
    media.play()?.catch(() => this.askToPlay(media))
  }

  askToPlay(media) {
    if (!this.hasPlayCueTarget || media !== this.audio) return

    this.playCueTarget.hidden = false
    this.audioPanelTarget.classList.add("vm-session-audio--waiting")
    media.addEventListener("playing", () => {
      this.playCueTarget.hidden = true
      this.audioPanelTarget.classList.remove("vm-session-audio--waiting")
    }, { once: true })
  }

  forgetAudioMode() {
    const url = new URL(window.location.href)
    if (!url.searchParams.has("mode")) return

    url.searchParams.delete("mode")
    window.history.replaceState(window.history.state, "", url)
  }

  get listening() {
    return this.modeValue === "audio"
  }

  get liveMedia() {
    return this.listening ? this.audio : this.video
  }

  get video() {
    return this.hasVideoPanelTarget ? this.videoPanelTarget.querySelector(MEDIA_SELECTOR) : null
  }

  get audio() {
    return this.hasAudioPanelTarget ? this.audioPanelTarget.querySelector(MEDIA_SELECTOR) : null
  }
}
